import { useContext, useEffect, useRef } from 'react'

import {
  UNSAFE_NavigationContext,
  useLocation,
  useNavigate,
  useNavigationType
} from 'react-router-dom'

type Action = 'PUSH' | 'POP' | 'REPLACE'

type Path = string

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LocationState = any

interface Location<S = LocationState> {
  pathname: string
  search: string
  hash: string
  state: S
  key?: string
}

interface LocationDescriptorObject<S = LocationState> {
  pathname?: string
  search?: string
  hash?: string
  state?: S
  key?: string
}

interface TransitionPromptHook<S = LocationState> {
  (location: Location<S>, action: Action): string | false | void
}

type UnregisterCallback = () => void

interface LocationListener<S = LocationState> {
  (location: Location<S>, action: Action): void
}

export interface History<S = LocationState> {
  length: number
  action: Action
  location: Location<S>
  push(path: Path, state?: S): void
  push(location: LocationDescriptorObject<S>): void
  replace(path: Path, state?: S): void
  replace(location: LocationDescriptorObject<S>): void
  go(n: number): void
  goBack(): void
  goForward(): void
  block(prompt?: boolean | string | TransitionPromptHook<S>): UnregisterCallback
  listen(listener: LocationListener<S>): UnregisterCallback
  createHref(location: LocationDescriptorObject<S>): string
}

// ============================================================
// Module-level blocking infrastructure.
//
// We patch the navigator (push/replace) and intercept popstate
// at the module level so blocking works regardless of which
// component initiated the navigation. This replicates how the
// v5 history package worked — a single block on the shared
// history instance affected all navigation app-wide.
// ============================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BlockPrompt = boolean | string | ((...args: any[]) => any)

let activePrompt: BlockPrompt | null = null
let activeBlockId = 0
let nextBlockId = 0

// Snapshot of the last "committed" history entry so we can
// restore it when a popstate is blocked.
let savedHistoryState: unknown = window.history.state
let savedHref: string = window.location.href

function updateSaved() {
  savedHistoryState = window.history.state
  savedHref = window.location.href
}

function evaluateBlock(
  nextLoc: {
    pathname: string
    search: string
    hash: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state: any
  },
  nextAction: string
): boolean {
  const prompt = activePrompt
  if (!prompt) return false

  if (typeof prompt === 'function') {
    const result = prompt(nextLoc, nextAction)
    if (typeof result === 'string') {
      return !window.confirm(result)
    }
    return result === false
  }

  if (typeof prompt === 'string') {
    return !window.confirm(prompt)
  }

  return !window.confirm('Are you sure you want to leave this page?')
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveTo(to: any): {
  pathname: string
  search: string
  hash: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any
} {
  if (typeof to === 'string') {
    try {
      const url = new URL(to, window.location.origin)
      return {
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
        state: undefined
      }
    } catch {
      return {
        pathname: to,
        search: '',
        hash: '',
        state: undefined
      }
    }
  }
  return {
    pathname: to?.pathname ?? window.location.pathname,
    search: to?.search ?? '',
    hash: to?.hash ?? '',
    state: to?.state
  }
}

// -- Popstate blocker (browser back / forward) --
// Registered once at module level, before react-router's
// listener (which is set up during React render). Since
// listeners on the same target fire in registration order,
// ours fires first. stopImmediatePropagation() prevents
// react-router from ever seeing a blocked popstate.

window.addEventListener('popstate', e => {
  if (!activePrompt) {
    updateSaved()
    return
  }

  const nextLoc = {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state: (window.history.state as any)?.usr
  }

  if (evaluateBlock(nextLoc, 'POP')) {
    // Block: prevent react-router from seeing this event
    // and synchronously restore the previous URL + state.
    e.stopImmediatePropagation()
    window.history.pushState(savedHistoryState, '', savedHref)
  } else {
    updateSaved()
  }
})

// -- Navigator patcher --
// Patches navigator.push / navigator.replace so that ALL
// programmatic navigation (including useNavigate()) is
// subject to the active block.

let navigatorPatched = false
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let originalPush: any = null
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let originalReplace: any = null

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function patchNavigator(nav: any) {
  if (navigatorPatched) return
  navigatorPatched = true

  originalPush = nav.push.bind(nav)
  originalReplace = nav.replace.bind(nav)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nav.push = (to: any, state?: any) => {
    if (activePrompt) {
      const loc = resolveTo(to)
      if (evaluateBlock(loc, 'PUSH')) return
    }
    originalPush(to, state)
    updateSaved()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nav.replace = (to: any, state?: any) => {
    if (activePrompt) {
      const loc = resolveTo(to)
      if (evaluateBlock(loc, 'REPLACE')) return
    }
    originalReplace(to, state)
    updateSaved()
  }
}

// ============================================================
// The hook
// ============================================================

/**
 * Polyfill for the router v5 history object on v6.
 * We shouldn't do this, really, but we have to. We have many microfrontends
 * and it is just unrealistic to expect all of them to ditch the history
 * requirement all at the same time. So for now, we replicate the object.
 * -- This was done on 01.04.2026, during the migration from
 * react-router-dom v5 to v6. When all microfrontends are migrated properly,
 * this can be removed.
 */
export const useHistory = <S = LocationState>(): History<S> => {
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const navigationType = useNavigationType()
  const { navigator } = useContext(UNSAFE_NavigationContext)

  // Patch the navigator once so push/replace are blocked.
  patchNavigator(navigator)

  const listenersRef = useRef(new Set<LocationListener<S>>())
  const blockIdRef = useRef(0)
  const beforeUnloadHandlerRef = useRef<
    ((e: BeforeUnloadEvent) => void) | null
  >(null)

  const location: Location<S> = {
    pathname: routerLocation.pathname,
    search: routerLocation.search,
    hash: routerLocation.hash,
    state: routerLocation.state as S,
    key: routerLocation.key
  }

  const action: Action = navigationType as Action

  // Keep saved state in sync with router location changes
  // (handles navigations that were allowed through).
  useEffect(() => {
    updateSaved()
  }, [routerLocation.key])

  const isInitialRender = useRef(true)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    listenersRef.current.forEach(listener => listener(location, action))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.key])

  // Clean up on unmount.
  useEffect(() => {
    return () => {
      // Clear block if this instance owns it.
      if (blockIdRef.current && blockIdRef.current === activeBlockId) {
        activePrompt = null
        activeBlockId = 0
      }
      if (beforeUnloadHandlerRef.current) {
        window.removeEventListener(
          'beforeunload',
          beforeUnloadHandlerRef.current
        )
      }
    }
  }, [])

  const historyRef = useRef<History<S>>(null!)
  if (historyRef.current === null) {
    historyRef.current = {} as History<S>
  }
  const h = historyRef.current

  h.length = window.history.length
  h.action = action
  h.location = location

  h.push = (pathOrLocation: Path | LocationDescriptorObject<S>, state?: S) => {
    if (typeof pathOrLocation === 'string') {
      navigate(pathOrLocation, { state })
    } else {
      const p = pathOrLocation.pathname || location.pathname
      const s = pathOrLocation.search || ''
      const hash = pathOrLocation.hash || ''
      navigate(`${p}${s}${hash}`, {
        state: pathOrLocation.state ?? state
      })
    }
  }

  h.replace = (
    pathOrLocation: Path | LocationDescriptorObject<S>,
    state?: S
  ) => {
    if (typeof pathOrLocation === 'string') {
      navigate(pathOrLocation, { replace: true, state })
    } else {
      const p = pathOrLocation.pathname || location.pathname
      const s = pathOrLocation.search || ''
      const hash = pathOrLocation.hash || ''
      navigate(`${p}${s}${hash}`, {
        replace: true,
        state: pathOrLocation.state ?? state
      })
    }
  }

  h.go = (n: number) => navigate(n)
  h.goBack = () => navigate(-1)
  h.goForward = () => navigate(1)

  h.listen = (listener: LocationListener<S>): UnregisterCallback => {
    listenersRef.current.add(listener)
    return () => {
      listenersRef.current.delete(listener)
    }
  }

  h.block = (
    prompt?: boolean | string | TransitionPromptHook<S>
  ): UnregisterCallback => {
    nextBlockId += 1
    const id = nextBlockId
    blockIdRef.current = id
    activePrompt = prompt ?? null
    activeBlockId = id

    // Remove previous beforeunload handler.
    if (beforeUnloadHandlerRef.current) {
      window.removeEventListener('beforeunload', beforeUnloadHandlerRef.current)
      beforeUnloadHandlerRef.current = null
    }

    // Set up beforeunload for tab close / reload.
    if (prompt) {
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault()
      }
      beforeUnloadHandlerRef.current = handler
      window.addEventListener('beforeunload', handler)
    }

    return () => {
      // Only clear if we're still the active block.
      if (activeBlockId === id) {
        activePrompt = null
        activeBlockId = 0
      }
      blockIdRef.current = 0
      if (beforeUnloadHandlerRef.current) {
        window.removeEventListener(
          'beforeunload',
          beforeUnloadHandlerRef.current
        )
        beforeUnloadHandlerRef.current = null
      }
    }
  }

  h.createHref = (loc: LocationDescriptorObject<S>): string => {
    const { pathname = '/', search = '', hash = '' } = loc
    return `${pathname}${search}${hash}`
  }

  return h
}
